import React, { useState, useEffect, useCallback, useContext } from 'react';
import styles from './styles.module.css';
import CustomerService from 'services/CustomerService';
import AlertHelper from 'helpers/AlertHelper';
import setDelay from 'helpers/LoadingDelay';
import { useParams, useHistory } from 'react-router-dom';
import Error from 'components/utils/Alert/Error';
import Success from 'components/utils/Alert/Success';
import DataTable from 'components/utils/DataTable';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { Grid, Typography, Button, Paper, TablePagination, Skeleton, Tooltip, IconButton, Box } from '@mui/material';
import 'date-fns';
import SyncIcon from '@mui/icons-material/Sync';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import CustomerSaleDialog from 'components/popups/CustomerSaleDialog';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AddPaymentDialog from 'components/popups/AddPaymentPopup';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { saveAs } from 'file-saver';
import { ShopContext } from 'Context/ShopContext';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import QrCodeIcon from '@mui/icons-material/QrCode';
import GenerateCustomerQrDialog from 'components/popups/GenerateCustomerQrDialog';
import ClearIcon from '@mui/icons-material/Clear';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import { ROUTES } from 'const';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import RefundTypePopup from 'components/popups/RefundTypePopup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import PrintIcon from '@mui/icons-material/Print';
import { BillFormatContext } from 'Context/BillFormatContext';
import { TerminalContext } from 'Context/TerminalContext';
import PaymentReceipt from 'components/utils/PrintDocument/PaymentReceipt';
import SaleInvoice from 'components/utils/InvoiceDownload/SaleInvoice';
import { createPortal } from 'react-dom';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';

function CustomerDetails() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { t: tReport } = useTranslation('report');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { shop } = useContext(ShopContext);
  const { getBillFormat } = useContext(BillFormatContext);
  const { selectedTerminal } = useContext(TerminalContext);
  const history = useHistory();

  const [unsetData, setUnsetData] = useState(null);
  const [loadingIndicator, setLoadingIndicator] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [customerJournals, setCustomerJournals] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [paymentReceiptToPrint, setPaymentReceiptToPrint] = useState(null);
  const [billFormatToPrint, setBillFormatToPrint] = useState(null);
  const [isEnableSearchRefreshButton, setIsEnableSearchRefreshButton] = useState(true);
  const [customerJournalsPagination, setCustomerJournalsPagination] = useState({
    page: 0,
    offset: 0,
    limit: 50,
    count: 0,
  });
  const [loyaltyJournalsPagination, setLoyaltyJournalsPagination] = useState({
    page: 0,
    offset: 0,
    limit: 50,
    count: 0,
  });

  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();

  const [isShowGenerateButton, setIsShowGenerateButton] = useState(false);
  const [prevFilterData, setPrevFilterData] = useState({
    fromDate,
    toDate,
  });
  const [selectedSale, setSelectedSale] = useState(null);
  const [paymentTab, setPaymentTab] = useState(false);
  const [loyaltyJournals, setLoyaltyJournals] = useState(null);
  const [showCustomerQr, setShowCustomerQr] = useState(false);

  const { customerId } = useParams();

  const formatJournalDescription = (item) => {
    if (!item) return '';
    let desc = item.description || item.note || '';
    if (desc.includes(' (Sale update at')) {
      desc = desc.split(' (Sale update at')[0];
    }
    return desc;
  };

  const CSV_COLUMNS = {
    id: tCommon('ID'),
    sale_id: tCommon('Sale ID'),
    date: {
      title: tCommon('Date'),
      getValue: (item) => {
        if (!item || !item.time) {
          return '';
        }
        return DateTimeHelpers.convertDateToIsoDMY(item.time);
      },
    },
    time: {
      title: tCommon('Time'),
      getValue: (item) => {
        if (!item || !item.time) {
          return '';
        }
        const date = item.time.split('T')[1].split('.')[0];
        return DateTimeHelpers.formatServerTimeSegment(date);
      },
    },
    description: {
      title: tCommon('Description'),
      getValue: (item) => formatJournalDescription(item),
    },
    debit: {
      title: tCommon('Debit'),
      getValue: (item) => {
        if (item?.is_opening_balance || item?.debit === '') return '';
        return ShopsHelper.getAmountFormatted(shop, item?.debit || 0);
      },
    },
    credit: {
      title: tCommon('Credit'),
      getValue: (item) => {
        if (item?.is_opening_balance || item?.credit === '') return '';
        return ShopsHelper.getAmountFormatted(shop, item?.credit || 0);
      },
    },
    balance: {
      title: tCommon('Balance'),
      getValue: (item) => {
        return ShopsHelper.getAmountFormatted(shop, item?.balance || 0);
      },
    },
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(customerJournals, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `${tCommon('Transactions').toUpperCase()}-${dateTime}.csv`);

    postAlertMessage({
      text: tCommon('Exported to excel successfully'),
      type: 'success',
    });
  };

  const handleEditSaleClick = (data) => history.push(`${ROUTES.MANAGE_SALES}?id=${data.sale_id}`);
  const handleUnsetSaleClick = (data) => setUnsetData(data);
  const handleUnsetSale = async (refuntType) => {
    setLoadingIndicator(true);
    try {
      await CustomerService.removeCustomerFromSale(unsetData.sale_id, { refund_type: refuntType });
      getCustomerJournals();
      postAlertMessage({
        text: tOperations('Sales unassigned successfully'),
        type: 'success',
      });
      setUnsetData(null);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setDelay(setLoadingIndicator);
  };

  const getBalance = (data) => {
    const balance = ShopsHelper.getAmountFormatted(shop, data?.balance);
    let balanceStyle;
    if (balance >= 0) balanceStyle = { color: 'green' };
    else balanceStyle = { color: 'red' };
    return (
      <Typography variant="body2" style={balanceStyle} gutterBottom>
        {formatBalanceWithCr(balance)}
      </Typography>
    );
  };

  const getHeaderData = (type) => {
    const headerData = [
      {
        label: tCommon('Date'),
        id: 'Date',
        type: 'callback',
        viewRender: (item) => {
          if (!item || item?.is_opening_balance || !item.created_at) {
            return '';
          }
          return `${DateTimeHelpers.convertDateToIsoDMY(item.created_at)} ${DateTimeHelpers.convertTo12HourTime(
            item.created_at
          )}`;
        },
      },
      {
        label: tCommon('Transaction Date'),
        id: 'time',
        type: 'callback',
        viewRender: (item) => {
          if (!item || item?.is_opening_balance || (!item.payment_time && !item.time)) {
            return '';
          }
          return `${DateTimeHelpers.convertDateToIsoDMY(
            item.payment_time || item.time
          )} ${DateTimeHelpers.convertTo12HourTime(item.payment_time || item.time)}`;
        },
      },
      {
        label: tCommon('Description'),
        id: 'description',
        type: 'callback',
        viewRender: (item) => formatJournalDescription(item),
      },
      {
        label: tCommon('Debit'),
        id: 'credit',
        type: 'callback',
        viewRender: (item) => {
          if (item?.is_opening_balance || item?.credit === '') return '';
          return ShopsHelper.getAmountFormatted(shop, item?.credit || 0);
        },
      },
      {
        label: tCommon('Credit'),
        id: 'debit',
        type: 'callback',
        viewRender: (item) => {
          if (item?.is_opening_balance || item?.debit === '') return '';
          return ShopsHelper.getAmountFormatted(shop, item?.debit || 0);
        },
      },
      {
        label: tCommon('Balance'),
        id: 'balance',
        type: 'callback',
        viewRender: (data) => getBalance(data),
      },
    ];

    if (type === 'customerLog' || type === 'loyaltyLog') {
      headerData.push({
        label: tCommon('Actions'),
        id: 'unset_sale',
        type: 'callback',
        viewRender: (row) => {
          if (row?.is_opening_balance) {
            return null;
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {row?.sale_id ? (
                <SaleInvoice bill={{ id: row.sale_id }} showOptions={false} />
              ) : (
                <Tooltip title={tCommon('Print')}>
                  <IconButton
                    onClick={(e) => {
                      handlePaymentReceipt(row);
                      e.stopPropagation();
                    }}
                  >
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              )}

              {row?.sale_id && (
                <>
                  <Tooltip title={tCommon('View Details')}>
                    <IconButton
                      onClick={(e) => {
                        toggleViewSale(row);
                        e.stopPropagation();
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  {type === 'customerLog' && (
                    <Tooltip title={tCommon('Unset sale')}>
                      <IconButton
                        onClick={(e) => {
                          handleUnsetSaleClick(row);
                          e.stopPropagation();
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={tCommon('Edit sale')}>
                    <IconButton
                      onClick={(e) => {
                        handleEditSaleClick(row);
                        e.stopPropagation();
                      }}
                    >
                      <ModeEditOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        },
      });
    }
    return headerData;
  };

  const getCustomerJournals = async ({
    _fromDate = fromDate,
    _toDate = toDate,
    _offset = customerJournalsPagination.offset,
    _limit = customerJournalsPagination.limit,
  } = {}) => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('CUSTOMER_DETAILS');

    const start = _fromDate || dynamicStartDate;
    const end = _toDate || dynamicEndDate;

    setLoadingIndicator(true);
    try {
      const params = {
        start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        offset: _offset,
        limit: _limit,
        search: filterText,
      };

      const res = await CustomerService.getCustomerJournals(customerId, params);
      let journals = [...res?.data];

      const firstJournal = journals[0];
      if (firstJournal) {
        journals = [
          {
            is_opening_balance: true,
            description: tCommon('Opening balance'),
            credit: '',
            debit: '',
            balance: firstJournal.balance - firstJournal.credit + firstJournal.debit,
          },
          ...journals,
        ];
      }
      setCustomerJournals(journals);
      setCustomerJournalsPagination((prev) => ({
        ...prev,
        limit: _limit,
        offset: _offset,
        count: res?.count,
      }));
      setIsEnableSearchRefreshButton(false);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoadingIndicator(false);
  };

  const getCustomerLoyaltyLogs = async ({
    _fromDate = fromDate,
    _toDate = toDate,
    _offset = loyaltyJournalsPagination.offset,
    _limit = loyaltyJournalsPagination.limit,
  } = {}) => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('CUSTOMER_DETAILS');

    const start = _fromDate || dynamicStartDate;
    const end = _toDate || dynamicEndDate;

    setLoadingIndicator(true);
    try {
      const params = {
        start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        offset: _offset,
        limit: _limit,
        search: filterText,
      };
      const res = await CustomerService.getCustomerLoyaltyLogs(customerId, params);
      let journals = [...res?.data];
      const firstJournal = journals[0];
      if (firstJournal) {
        journals = [
          {
            is_opening_balance: true,
            description: tCommon('Opening balance'),
            credit: '',
            debit: '',
            balance: firstJournal.balance - firstJournal.credit + firstJournal.debit,
          },
          ...journals,
        ];
      }
      setLoyaltyJournals(journals);
      setLoyaltyJournalsPagination((prev) => ({
        ...prev,
        limit: _limit,
        offset: _offset,
        count: res?.count,
      }));
      setIsEnableSearchRefreshButton(false);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoadingIndicator(false);
  };

  const getCustomerDetails = async () => {
    try {
      const res = await CustomerService.getCustomer(customerId);
      setCustomer(res);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
  };

  const togglePaymentTab = () => setPaymentTab((prevValue) => !prevValue);

  const toggleViewSale = async (row) => {
    if (!row?.sale_id) {
      return setSelectedSale(null);
    }
    setLoadingIndicator(true);
    try {
      const res = await CustomerService.getCustomerSaleDetails(row?.sale_id);
      setSelectedSale(res);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoadingIndicator(false);
  };
  const handlePaymentReceipt = async (row) => {
    setLoadingIndicator(true);
    try {
      let billFormat = null;
      if (selectedTerminal?.id) {
        billFormat = await getBillFormat(selectedTerminal.id);
      }
      setBillFormatToPrint(billFormat);
      setPaymentReceiptToPrint(row || {});
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    } finally {
      setLoadingIndicator(false);
    }
  };
  useEffect(() => {
    if (paymentReceiptToPrint) {
      const originalTitle = document.title;
      document.title = `Customer Receipt - ${customer?.name || ''}`;

      document.body.classList.add('printing-invoice');
      setTimeout(() => {
        window.print();
        document.body.classList.remove('printing-invoice');
        document.title = originalTitle;
        setPaymentReceiptToPrint(null);
        setBillFormatToPrint(null);
      }, 500);
    }
  }, [paymentReceiptToPrint, customer?.name]);

  const appPayment = async (data) => {
    setLoadingIndicator(true);
    try {
      await CustomerService.addPaymentToCustomer(customerId, data);
      togglePaymentTab();
      setCustomerJournalsPagination({
        ...customerJournalsPagination,
        page: 0,
        offset: 0,
      });
      getCustomerDetails();
      getCustomerJournals({ _offset: 0 });
      AlertHelper.setShortMessage(setSuccessMsg, tCommon('Payment added successfully'));
      return true;
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
      return false;
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleChangeCustomerPage = (event, newPage) => {
    let offset = newPage * customerJournalsPagination.limit;
    setCustomerJournalsPagination((prev) => ({
      ...prev,
      page: newPage,
      offset: offset,
    }));
    getCustomerJournals({ _offset: offset });
  };

  const handleChangeCustomerRowsPerPage = (event) => {
    const pageRowLimit = parseInt(event.target.value, 10);
    setCustomerJournalsPagination((prev) => ({
      ...prev,
      limit: pageRowLimit,
      page: 0,
      offset: 0,
    }));
    getCustomerJournals({ _limit: pageRowLimit, _offset: 0 });
  };

  const handleChangeLoyltyrPage = (event, newPage) => {
    let offset = newPage * loyaltyJournalsPagination.limit;
    setLoyaltyJournalsPagination((prev) => ({
      ...prev,
      page: newPage,
      offset: offset,
    }));
    getCustomerLoyaltyLogs({ _offset: offset });
  };

  const handleChangeLoyltyRowsPerPage = (event) => {
    const pageRowLimit = parseInt(event.target.value, 10);
    setLoyaltyJournalsPagination((prev) => ({
      ...prev,
      limit: pageRowLimit,
      page: 0,
      offset: 0,
    }));
    getCustomerLoyaltyLogs({ _limit: pageRowLimit, _offset: 0 });
  };

  const handleSearch = () => {
    setCustomerJournalsPagination({
      ...customerJournalsPagination,
      page: 0,
      offset: 0,
    });
    setLoyaltyJournalsPagination({
      ...loyaltyJournalsPagination,
      page: 0,
      offset: 0,
    });
    getCustomerJournals({ _offset: 0 });
    getCustomerLoyaltyLogs({ _offset: 0 });
  };

  const generateCustomerDetails = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('CUSTOMER_DETAILS');
    setFromDate(dynamicStartDate);
    setToDate(dynamicEndDate);
    getCustomerJournals();
    getCustomerLoyaltyLogs();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      fromDate: dynamicStartDate,
      toDate: dynamicEndDate,
    });
  };

  const handleGoBack = () => {
    history.goBack({
      pathname: ROUTES.CUSTOMERS,
      state: history.location.state,
    });
  };

  const isAnyChangeOnCustomerDetailsFilters = useCallback(() => {
    return fromDate !== prevFilterData.fromDate || toDate !== prevFilterData.toDate;
  }, [fromDate, toDate, prevFilterData.fromDate, prevFilterData.toDate]);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnCustomerDetailsFilters());
  }, [isAnyChangeOnCustomerDetailsFilters, isShowGenerateButton]);

  useEffect(() => {
    getCustomerDetails();
    generateCustomerDetails();
    getCustomerLoyaltyLogs();
    //eslint-disable-next-line
  }, [customerId]);

  return (
    <div className={styles.contentWrapper}>
      <div className={`${styles.titleSec} no-print`}>
        <Box sx={{ display: 'flex', mr: 1.25 }}>
          <ArrowBackIcon onClick={handleGoBack} sx={{ cursor: 'pointer' }} />
        </Box>
        <h2 className={styles.title}>
          {tCommon('Transaction')}
          <span className={styles.menuTitle}>{tCommon('Details & History')}</span>
        </h2>
        {shop && shop?.customer_support_validity !== null && (
          <Box className="buttons" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Export As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={toCsvBtnPressed}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('CSV')}
              </Button>
            </div>
            <div style={{ justifyContent: 'flex-end', marginLeft: 10 }}>
              <Box sx={{ pb: 0.5 }}>
                <label className={styles.label}>{tCommon('Generate QR')}</label>
              </Box>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#d5d5d5' }}
                onClick={() => {
                  setShowCustomerQr(customer);
                }}
              >
                <QrCodeIcon sx={{ fontSize: 21 }} />
              </Button>
            </div>
          </Box>
        )}
      </div>
      {shop && shop.customer_support_validity == null && (
        <RestrictionInfo
          title={tOperations('No Customer Support')}
          content={tOperations(
            'You have no customer support feature enabled. Please contact support team to enable customer support feature.'
          )}
        />
      )}
      {shop && shop.customer_support_validity !== null && (
        <>
          <div className={styles.changeable}>
            <Grid container spacing={2}>
              <Grid item xs={12} className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tReport('Customer Transactions Details for {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(fromDate),
                    to: DateTimeHelpers.convertDateToDMY(toDate),
                  })}
                </h2>
              </Grid>

              <Grid item xs={12} className={styles.actionButtons}>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs={6} sm={3}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Customer Payment')}</label>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="small"
                      style={{ backgroundColor: '#ff851b', height: 40 }}
                      className={styles.actionBtn}
                      onClick={togglePaymentTab}
                    >
                      <RequestQuoteOutlinedIcon className={styles.actionBtnIcon} />
                      {tCommon('Add Payment')}
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Search Transactions')}</label>
                    </div>
                    <div className={styles.searchSec}>
                      <input
                        type="text"
                        value={filterText}
                        onChange={(e) => {
                          setFilterText(e.target.value);
                          setIsEnableSearchRefreshButton(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSearch();
                        }}
                        className={styles.searchInput}
                        placeholder={tCommon('Search Transactions')}
                      />
                      <Tooltip title="Search">
                        <IconButton
                          className={styles.iconBtn}
                          onClick={handleSearch}
                          disabled={!isEnableSearchRefreshButton}
                        >
                          <SyncIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <DateTimeRange
                      fullWidth
                      storageKey="CUSTOMER_DETAILS"
                      setFilterFromDate={setFromDate}
                      setFilterToDate={setToDate}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Generate Report')}</label>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      className={styles.actionBtn}
                      style={{ backgroundColor: '#00a65a', height: 40 }}
                      onClick={generateCustomerDetails}
                      disabled={!isShowGenerateButton}
                    >
                      <SyncIcon className={styles.actionBtnIcon} />
                      {tCommon('Generate')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>

          <Box className={styles.changeable} style={{ paddingBottom: '15px' }}>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Name')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? <Skeleton variant="text" width="60%" animation="wave" /> : customer?.name || '-'}
                </h3>
              </Grid>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Balance')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? (
                    <Skeleton variant="text" width="40%" animation="wave" />
                  ) : (
                    formatBalanceWithCr(customer?.balance || 0)
                  )}
                </h3>
              </Grid>
            </Grid>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Mobile')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? (
                    <Skeleton variant="text" width="50%" animation="wave" />
                  ) : (
                    customer?.mobile || '-'
                  )}
                </h3>
              </Grid>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Credit Limit')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? (
                    <Skeleton variant="text" width="30%" animation="wave" />
                  ) : (
                    formatBalanceWithCr(customer?.credit_limit || 0)
                  )}
                </h3>
              </Grid>
            </Grid>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Email')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? <Skeleton variant="text" width="70%" animation="wave" /> : customer?.email || '-'}
                </h3>
              </Grid>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Points')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? <Skeleton variant="text" width="20%" animation="wave" /> : customer?.points || 0}
                </h3>
              </Grid>
            </Grid>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Location')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? (
                    <Skeleton variant="text" width="50%" animation="wave" />
                  ) : (
                    customer?.location || '-'
                  )}
                </h3>
              </Grid>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Loyalty Card No')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator ? (
                    <Skeleton variant="text" width="40%" animation="wave" />
                  ) : (
                    customer?.loyalty_card_no || '-'
                  )}
                </h3>
              </Grid>
            </Grid>
          </Box>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}
          {successMsg && (
            <div className={styles.marginTop}>
              <Success title={successMsg} />
            </div>
          )}

          {/* TRANSACTOINS SECTION */}
          <Grid className={styles.changeable}>
            <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
              <span className={styles.subTitle}>{tCommon('Transaction History')}</span>
            </div>
            {(customerJournals && customerJournals.length) || loadingIndicator ? (
              <>
                <Paper className={styles.tableWrapper}>
                  <div className={styles.tableContainer}>
                    <Grayout open={isShowGenerateButton} />
                    <DataTable
                      columns={getHeaderData('customerLog')}
                      rows={customerJournals || []}
                      isLoading={loadingIndicator}
                    />
                  </div>
                  <TablePagination
                    rowsPerPageOptions={[50, 100]}
                    component="div"
                    count={customerJournalsPagination.count}
                    rowsPerPage={customerJournalsPagination.limit}
                    page={customerJournalsPagination.page}
                    onPageChange={handleChangeCustomerPage}
                    onRowsPerPageChange={handleChangeCustomerRowsPerPage}
                  />
                </Paper>
              </>
            ) : (
              !loadingIndicator && (
                <Info
                  title={tOperations('No transactions to list')}
                  content={tOperations(
                    'You have no transactions to list with current filter configuration. Please clear the filters or make some sale'
                  )}
                />
              )
            )}
          </Grid>

          {/* LOYALTY POINT SECTION */}
          <Grid className={styles.changeable}>
            <div className={styles.headTitle}>
              <span className={styles.subTitle}>Loyalty Points History</span>
            </div>
            {(loyaltyJournals && loyaltyJournals.length) || loadingIndicator ? (
              <>
                <Paper className={styles.tableWrapper}>
                  <div className={styles.tableContainer}>
                    <Grayout open={isShowGenerateButton} />
                    <DataTable
                      columns={getHeaderData('loyaltyLog')}
                      rows={loyaltyJournals || []}
                      isLoading={loadingIndicator}
                    />
                  </div>
                  <TablePagination
                    rowsPerPageOptions={[50, 100]}
                    component="div"
                    count={loyaltyJournalsPagination.count}
                    rowsPerPage={loyaltyJournalsPagination.limit}
                    page={loyaltyJournalsPagination.page}
                    onPageChange={handleChangeLoyltyrPage}
                    onRowsPerPageChange={handleChangeLoyltyRowsPerPage}
                  />
                </Paper>
              </>
            ) : (
              !loadingIndicator && (
                <Info
                  title={'No loyalty history to list'}
                  content={
                    'You have no loyalty history to list with current filter configuration. Please clear the filters or make some sale'
                  }
                />
              )
            )}
          </Grid>

          {selectedSale && <CustomerSaleDialog toggleItemsTab={toggleViewSale} rowData={selectedSale} />}
          {paymentTab && (
            <AddPaymentDialog
              togglePaymentTab={togglePaymentTab}
              addPaymentToCustomer={appPayment}
              currentBalance={customer?.balance || 0}
            />
          )}
          {showCustomerQr && (
            <GenerateCustomerQrDialog
              customer={customer}
              onClose={() => {
                setShowCustomerQr(false);
              }}
            />
          )}
          {unsetData && (
            <RefundTypePopup
              closeDialog={() => {
                handleUnsetSaleClick(null);
              }}
              handleRefundType={handleUnsetSale}
              subject={tCommon('Sales')}
            />
          )}
        </>
      )}
      {paymentReceiptToPrint &&
        createPortal(
          <PaymentReceipt
            data={paymentReceiptToPrint}
            shop={shop}
            billFormat={billFormatToPrint}
            customer={customer}
          />,
          document.body
        )}
    </div>
  );
}

export default withConsoleBase(CustomerDetails);
